import React, { useState, useEffect, useRef, memo } from "react";
import EmojiPicker from "../Common/EmojiPicker";
import { useLiveStreamAppContext } from "../ContextStore/Store";
import axios from "axios";
import MessageDisplay from "../Sub_components/MessageDisplay";
import DataChanneling from "../GeneralFunctions/DataChanneling";

const ChatScreen = ({
  communicationUserId,
  userName,
  role,
  messages,
  onClose,
  setChatColor,
  chatColor,
  navbarOptionMenu
}) => {
  const {
    chatThreadClientRef,
    __appConfirmUniqueID,
    __merchantId,
    __userId,
    appInsightTrack,
    setAttendees,
    isChatDisabled,
    setIsChatDisabled,
    toggleStates,
    setErrorMessage,
    setInformationContent,
    setLoaderAnimation,
    apiUrlPost,
    isMobile
  } = useLiveStreamAppContext();

  const { sendActionToAttendee } = DataChanneling();
  const [newMessage, setNewMessage] = useState("");
  const [newMessageEdit, setNewMessageEdit] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [isEditEmojiPicker, setIsEditEmojiPicker] = useState(false);
  const chatScrollRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);
  const validationMessageRef = useRef(null);

  const headers = {
    "Content-Type": "application/json",
    userId: __userId,
    merchantId: __merchantId
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  const closeEmojiPicker = () => {
    setShowEmojiPicker(false);
    setIsEditEmojiPicker(false);
  };

  const scrollToBottom = () => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
    if (chatColor && navbarOptionMenu === "chat") setChatColor(false);
  }, [navbarOptionMenu, chatColor, messages]);

  useEffect(() => {
    // Scroll when EmojiPicker is opened
    if (isEditEmojiPicker) {
      scrollToBottom();
    }
  }, [isEditEmojiPicker]);

  const handleDeleteMessage = async (messageId) => {
    const originalMessage = messages.find((msg) => msg.id === messageId);
    try {
      await chatThreadClientRef.current.sendMessage({
        content: "",
        senderDisplayName: userName,
        metadata: {
          customType: "DELETE_CHAT",
          senderUserId: communicationUserId.communicationUserId,
          targetUserId: "",
          messageId: messageId,
        },
      });
      const timestamp = new Date().toISOString();
      const payload = {
        appConfirmUniqueId: __appConfirmUniqueID,
        message: originalMessage.content.message,
        messageId: messageId,
        communicationUserId: communicationUserId.communicationUserId,
        isDeleted: true,
        time: timestamp,
        strTime: timestamp,
      };

      await axios.post(
        `${apiUrlPost}managechathistory`,
        payload,
        { headers }
      );

      // Dispose or reset sensitive data after use
      payload.appConfirmUniqueId = null;
      payload.message = null;
      payload.messageId = null;
      payload.communicationUserId = null;
    } catch (error) {
      console.error("Error permanently deleting message:", error);
      setLoaderAnimation(false);
      setErrorMessage(error.message);
      appInsightTrack(__merchantId,__userId,"API Error managechathistory -> DELETE","",error);      
    }
  };

  const messageDeleted = (message) => {
    return (
      <>
        <div className="undoMsgNote">
          This message has been deleted.{" "}
          <a
            href="javascript:void(0);"
            onClick={() => handleUndoDelete(message.id)}
          >
            Undo
          </a>
        </div>
      </>
    );
  };

  const handleDropDown = (id) => {
    if (!isMobile) return;

    const avatarWraps = document.querySelectorAll(
      "#chatText .chat-avatar-wrap"
    );

    avatarWraps.forEach((avatarWrap) => {
      // Find the child element with the 'chatDropDownMenu' class within each 'chat-avatar-wrap'
      const dropDownMenu = avatarWrap.querySelector(".chatDropDownMenu");

      // If the 'chatDropDownMenu' class exists, remove it
      if (dropDownMenu) {
        dropDownMenu.classList.remove("chatDropDownMenu");
      }
    });

    const targetElement = document.getElementById(id);

    if (targetElement?.classList?.contains("chatDropDownMenu")) {
      targetElement.classList.remove("chatDropDownMenu");
    } else {
      targetElement.classList.add("chatDropDownMenu");
    }
  };

  const handleUndoDelete = async (messageId) => {
    const originalMessage = messages.find((msg) => msg.id === messageId);
    try {
      chatThreadClientRef.current.sendMessage({
        content: "",
        senderDisplayName: userName,
        metadata: {
          customType: "UNDO_CHAT",
          senderUserId: communicationUserId.communicationUserId,
          targetUserId: "",
          messageId: messageId,
        },
      });
      const timestamp = new Date().toISOString();
      const payload = {
        appConfirmUniqueId: __appConfirmUniqueID,
        message: originalMessage.content.message,
        messageId: messageId,
        communicationUserId: communicationUserId.communicationUserId,
        isDeleted: false,
        time: timestamp,
        strTime: timestamp,
      };

      await axios.post(
        `${apiUrlPost}managechathistory`,
        payload,
        { headers }
      );
      // Dispose or reset sensitive data after use
      payload.appConfirmUniqueId = null;
      payload.message = null;
      payload.messageId = null;
      payload.communicationUserId = null;
    } catch (error) {
      console.error("Error while undoing message:", error);
      setLoaderAnimation(false);
      setErrorMessage(error.message);
      appInsightTrack(__merchantId,__userId,"API Error managechathistory -> UNDO","",error);      
    }
  };

  const handleEditMessage = async (messageId, content) => {
    try {
      await chatThreadClientRef.current.updateMessage(messageId, {
        content: content,
      });
      const timestamp = new Date().toISOString();
      const payload = {
        appConfirmUniqueId: __appConfirmUniqueID,
        message: content,
        messageId: messageId,
        communicationUserId: communicationUserId.communicationUserId,
        isEdited: true,
        time: timestamp,
        strTime: timestamp,
      };

      await axios.post(
        `${apiUrlPost}managechathistory`,
        payload,
        { headers }
      );
      // Dispose or reset sensitive data after use
      payload.appConfirmUniqueId = null;
      payload.message = null;
      payload.messageId = null;
      payload.communicationUserId = null;
    } catch (error) {
      console.error("Error editing message:", error);
      setLoaderAnimation(false);
      setErrorMessage(error.message);
      appInsightTrack(__merchantId,__userId,"API Error managechathistory -> EDIT ","",error);      
    }
  };

  const handleSendMessage = async () => {
    setIsEditEmojiPicker(false);
    const trimmedMessage = editingMessageId
      ? newMessageEdit.trim()
      : newMessage.trim();

    // Check if the trimmed message is empty
    if (!trimmedMessage) {
      // Display the validation message
      if (validationMessageRef.current) {
        validationMessageRef.current.style.display = "block";
      }

      // Hide the validation message after 3 seconds
      setTimeout(() => {
        if (validationMessageRef.current) {
          validationMessageRef.current.style.display = "none"; // Hide the message
        }
      }, 3000);
      return; // Exit early if the message is empty
    }

    // Clear the validation message (if it was previously shown)
    if (validationMessageRef.current) {
      validationMessageRef.current.style.display = "none";
    }

    try {
      if (editingMessageId) {
        setEditingMessageId(null);
        await handleEditMessage(editingMessageId, trimmedMessage);
        setNewMessageEdit("");
      } else {
        setNewMessage("");
        const sentMessage = await chatThreadClientRef.current.sendMessage({
          content: trimmedMessage,
          senderDisplayName: userName,
          metadata: {
            customType: "NORMAL_CHAT",
            senderUserId: communicationUserId.communicationUserId,
            targetUserId: "",
          },
        });
        const timestamp = new Date().toISOString();
        const payload = {
          appConfirmUniqueId: __appConfirmUniqueID,
          message: trimmedMessage,
          messageId: sentMessage.id,
          communicationUserId: communicationUserId.communicationUserId,
          time: timestamp,
          strTime: timestamp,
        };

        await axios.post(
          `${apiUrlPost}managechathistory`,
          payload,
          { headers }
        );
        // Dispose or reset sensitive data after use
        payload.appConfirmUniqueId = null;
        payload.message = null;
        payload.messageId = null;
        payload.communicationUserId = null;
      }
    } catch (error) {
      setInformationContent({
        type: "alert",
        message: error.message,
      });
      if (error?.details?.CommunicationError?.Code === "Forbidden") {
      } else {
        console.error("Error sending message:", error);
      }
      appInsightTrack(__merchantId,__userId,"API Error managechathistory -> SEND","",error);      
    } finally {
      inputRef.current?.focus();
    }
  };

  const handleEmojiClickChat = (event) => {
    if (event.detail && event.detail.unicode) {
      const emojiData = event.detail;
      setNewMessage((prevMessage) => prevMessage + emojiData.unicode);
      inputRef.current?.focus();
    }
  };

  const handleChatAllButton = (data) => {
    setAttendees((prevAttendees) =>
      prevAttendees.map((attendee) => {
        return { ...attendee, isChatDisabled: data };
      })
    );
  };

  const toggleChat = () => {
    setIsChatDisabled((prev) => {
      const newChatState = !prev;
      if (newChatState) {
        sendActionToAttendee("ALL", "DISABLE_CHAT_FOR_ALL");
        handleChatAllButton(true);
      } else {
        sendActionToAttendee("ALL", "ENABLE_CHAT_FOR_ALL");
        handleChatAllButton(false);
      }
      return newChatState;
    });
  };

  return (
    <>
      <div className="panel-title-for-mob">
        <div className="backIcon" onClick={onClose}>
          <i className="icon font-awesome-normal-pro va-back-arrow"></i>
        </div>
        <div className="back-page-title">
          <span>Chat</span>
        </div>
      </div>
      <div className="chat-title-box">
        <div className="top-bar-chat">
          <div className="section-title">Chat</div>
          {role === "Presenter" && (
            <a href className="brd-btn-small" onClick={toggleChat}>
              {!toggleStates["EnableChat"] ? "Enable Chat" : "Disable Chat"}
            </a>
          )}
        </div>
        <div className="chat-middle-scroll" ref={chatScrollRef}>
          <div className="chat-opp scrollfirst">
            <MessageDisplay
              messages={messages}
              communicationUserId={communicationUserId}              
              handleDropDown={handleDropDown}
              role={role}
              messageDeleted={messageDeleted}
              setEditingMessageId={setEditingMessageId}
              handleDeleteMessage = {handleDeleteMessage}              
              editingMessageId={editingMessageId}
              validationMessageRef={validationMessageRef}
              newMessageEdit={newMessageEdit}
              setNewMessageEdit={setNewMessageEdit}
              handleSendMessage = {handleSendMessage}
              setIsEditEmojiPicker={setIsEditEmojiPicker}
              isEditEmojiPicker={isEditEmojiPicker}
              handleEmojiClickEdit={(event) => {
                if (event.detail && event.detail.unicode) {
                  const emojiData = event.detail;
                  setNewMessageEdit((prevMessage) => prevMessage + emojiData.unicode);
                }
              }}              
            />
          </div>
        </div>
        {showEmojiPicker && (
          <div className="emoji-picker" ref={emojiPickerRef}>
            <EmojiPicker
              handleEmojiClick={handleEmojiClickChat}
              closeEmojiPicker={closeEmojiPicker}
            />
          </div>
        )}
      </div>
      <div className="chat-message-area">
        <div ref={validationMessageRef}
          id="divChatMessageBlankValidation"
          className="divChatMessageBlankValidation"
          style={{ display: "none" }}
        >
          Please type a message to continue.
        </div>
        {
          <div
            className={`chat-input ${(!toggleStates["EnableChat"] || isChatDisabled) &&
              role !== "Presenter"
              ? "divDisabled"
              : ""
              }`}
          >
            <input
              type="text"
              ref={inputRef}
              className="chat-box"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={
                (!toggleStates["EnableChat"] || isChatDisabled) &&
                  role !== "Presenter"
                  ? "Chat is disabled by the Host"
                  : "Type a message..."
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            <i
              className={`emoji-picker-icon emoji-picker fa fa-smile-o`}
              style={{
                display:
                  (!toggleStates["EnableChat"] || isChatDisabled) &&
                    role !== "Presenter"
                    ? "none"
                    : "block",
              }}
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            ></i>
          </div>
        }
        {(!toggleStates["EnableChat"] || isChatDisabled) &&
          role !== "Presenter" ? (
          ""
        ) : (
          <a
            href="javascript:void(0);"
            className="send-btn"
            title="Send message"
            onClick={handleSendMessage}
          >
            <i
              className="chatIconSetting fas fa-arrow-circle-right"
              aria-hidden="true"
            ></i>
          </a>
        )}
      </div>
    </>
  );
};
export default memo(ChatScreen);
